/**
 * Customer-Scoped Domain API Client
 *
 * Routes through customer-scoped endpoints that auto-scope
 * to the authenticated customer's website.
 */

import { apiGet, apiPost, apiRequest } from './client';

// =============================================================================
// TYPES
// =============================================================================

export interface PurchasedDomain {
  id: string;
  domain_name: string;
  registrar: string;
  purchase_status: string;
  ns_propagation_status: string | null;
  purchase_price_cents: number;
  expires_at: string | null;
  website_id: string | null;
  created_at: string;
  provisioning_started_at: string | null;
  provisioning_completed_at: string | null;
  auto_renew: number; // 1 = enabled, 0 = disabled
}

interface PurchasedDomainsResponse {
  success: boolean;
  domains: PurchasedDomain[];
}

export interface DomainStatus {
  subdomain: string | null;
  customDomain: string | null;
  verificationStatus: string | null;
  connectedAt: string | null;
  source: string | null;
  projectName: string | null;
}

interface DomainStatusResponse {
  success: boolean;
  domain: DomainStatus;
}

interface SetDomainResponse {
  success: boolean;
  domain: string;
  verificationStatus: string;
}

export interface DnsInstructions {
  domain: string;
  cname: {
    type: string;
    name: string;
    target: string;
  };
  /**
   * Registrars refuse a CNAME at the root of a zone, so the apex is pointed at
   * www with the registrar's own forwarding rather than a DNS record.
   */
  apex: {
    type: string;
    from: string;
    to: string;
  };
  steps: string[];
}

interface DnsInstructionsResponse {
  success: boolean;
  instructions: DnsInstructions;
}

interface VerifyDomainResponse {
  success: boolean;
  message: string;
  domain?: string;
  status?: string;
  expected?: {
    cname: string;
  };
}

interface RemoveDomainResponse {
  success: boolean;
  message: string;
}

interface UpdateAutoRenewResponse {
  success: boolean;
  domain_id: string;
  domain_name: string;
  auto_renew: boolean;
}

// =============================================================================
// API FUNCTIONS
// =============================================================================

export function getDomainStatus(): Promise<DomainStatusResponse> {
  return apiGet<DomainStatusResponse>('/api/customer/domain/status');
}

export function getPurchasedDomains(): Promise<PurchasedDomainsResponse> {
  return apiGet<PurchasedDomainsResponse>('/api/customer/domains');
}

export function setCustomDomain(domain: string): Promise<SetDomainResponse> {
  return apiPost<SetDomainResponse>('/api/customer/domain/set', { domain });
}

export function getDnsInstructions(): Promise<DnsInstructionsResponse> {
  return apiGet<DnsInstructionsResponse>('/api/customer/domain/instructions');
}

export function verifyDomain(): Promise<VerifyDomainResponse> {
  return apiPost<VerifyDomainResponse>('/api/customer/domain/verify');
}

export function removeDomain(): Promise<RemoveDomainResponse> {
  return apiRequest<RemoveDomainResponse>('/api/customer/domain', {
    method: 'DELETE',
  });
}

export function updateDomainAutoRenew(domainId: string, autoRenew: boolean): Promise<UpdateAutoRenewResponse> {
  return apiRequest<UpdateAutoRenewResponse>(`/api/customer/domains/${domainId}/auto-renew`, {
    method: 'PATCH',
    body: JSON.stringify({ auto_renew: autoRenew }),
  });
}
